<template>
  <div>
    <div :class="containerClass">
      <div class="d-flex flex-column flex-sm-row align-items-sm-center mb-24pt mt-4" style="white-space: nowrap">
        <h2 class="mb-0 d-sm-block" v-if="!isMobSmallScreen">
          Skill Cards
          <i
            class="material-icons icon-16pt clickable-item"
            v-b-popover.hover.top="'Explore the skill cards of other students.'"
            >info</i
          >
        </h2>
        <h2 class="mb-4 d-sm-block" v-if="isMobSmallScreen" style="align-self: center;">
          Skill Cards
          <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="'Explore you own skill cards.'"
            >info</i
          >
        </h2>
        <div class="w-auto ml-sm-auto mb-sm-0 justify-content-end">
          <v-select
            id="skillmap"
            class="form-control v-select-custom"
            placeholder="select skill"
            style="min-width: 220px"
            v-model="filters.skillmap"
            :options="schoolSkillMaps"
            :reduce="opt => opt.id"
            :loading="isLoadingSkillmaps"
          ></v-select>
        </div>
      </div>

      <page-separator
        :title="
          !isLoading
            ? `showing ${totalSkillCards ? `${pageFromCount}-${pageToCount}` : 0} of ${totalSkillCards} Skill Cards`
            : ''
        "
      />

      <div class="page-section d-flex justify-content-center" v-if="!skillCard.length && !isLoading">
        <div class="card card-block card-stretch card-height col-md-8">
          <div class="card-body text-center">
            <i class="fas fa-exclamation-circle fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
            <h4>No Skill Cards Found</h4>

            <p class="text-muted">
              Couldn't find any skill cards.
            </p>
          </div>
        </div>
      </div>

      <b-skeleton-wrapper :loading="isLoading" v-else>
        <template #loading>
          <div class="row">
            <div class="col-lg-4 col-md-4 col-sm-6" v-for="item in [1, 2, 3]" :key="item">
              <div class="card card-sm card--elevated p-relative">
                <span class="js-image">
                  <b-skeleton-img width="100%" class="pb-0 mb-0"></b-skeleton-img>
                </span>

                <div class="card-body">
                  <div class="d-flex">
                    <div class="avatar avatar-sm rounded-circle o-hidden" style="border: 1px solid #dfe2e6;">
                      <b-skeleton-img class="avatar-img" width="160" height="100%"></b-skeleton-img>
                    </div>
                    <span class="ml-2 w-100">
                      <div class="card-title"><b-skeleton width="100%" /></div>
                      <div class="text-muted"><b-skeleton width="50%"></b-skeleton></div>
                    </span>
                  </div>
                </div>
                <div class="card-footer">
                  <div class="row justify-content-end mr-2">
                    <b-skeleton width="20%" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div class="row card-group-row mb-lg-12pt home-card">
          <div
            v-for="card in skillCard"
            :key="card.id"
            :id="`skill-${card.id}`"
            class="col-lg-4 col-md-4 col-sm-6 clickable-item mb-3"
          >
            <div
              style="height: 310px"
              class="card card-sm card--elevated mb-0 card-flip"
              :class="{ flipped: flippedCards[card.id] }"
            >
              <div class="card-flip-inner">
                <div class="card-flip-front">
                  <div class="d-flex flex-column justify-content-between" style="height: inherit">
                    <div @click.stop="toggleCardFlip(card.id)">
                      <img
                        :src="card.image ? card.image : require('@/assets/images/placeholders/default_image.jpeg')"
                        alt="skill demonstration"
                        style="border-top-left-radius: 0.5rem; border-top-right-radius: 0.5rem; height: 250px; object-fit: cover;"
                        class="career-card-img"
                      />
                      <b-btn class="flip-icon"><md-icon class="mr-2">repeat</md-icon>Click to flip</b-btn>
                    </div>

                    <div class="px-2 pb-2 d-flex justify-content-between align-items-center">
                      <div class="d-flex">
                        <user-avatar
                          slot="aside"
                          :user="card.student.user"
                          circle="md"
                          size="md"
                          :file-url="card.student.user.avatar_url"
                          variant="dark"
                        >
                        </user-avatar>

                        <div class="d-flex flex-column ml-2">
                          <div class="card-title">
                            {{ card.student.user.first_name }} {{ card.student.user.last_name }}
                          </div>
                          <div class="text-muted d-flex align-items-center mt-1">
                            <div
                              class="material-icons icon-16pt text-black-50 mr-1"
                              style="font-size: 14px; vertical-align: middle;"
                            >
                              access_time
                            </div>
                            <div style="line-height: 1">
                              {{ formatTime(card.created_at) }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="d-flex align-items-center">
                        <div
                          class="text-black-50"
                          @click.prevent="
                            get(likesCount[card.id], 'mylike') ? unlikeSkillCard(card.id) : likeSkillCard(card.id)
                          "
                        >
                          <span
                            class="material-icons icon-16pt"
                            :class="{ 'text-primary': get(likesCount[card.id], 'mylike') }"
                            >thumb_up</span
                          >
                          <span :class="{ 'text-primary': get(likesCount[card.id], 'mylike') }">
                            {{ $n(get(likesCount[card.id], 'like_count', 0)) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card-flip-back" style="height: 100%;" @click.stop="toggleCardFlip(card.id)">
                  <div class="d-flex flex-column justify-content-center  flex-wrap" style="height: 100%;">
                    <h5>{{ card.skill_map ? card.skill_map.title : 'ai skill card title' }}</h5>
                    <div
                      style="background-color: #dddddd; border-radius: 7px; max-height: 120px; max-width: 260px; overflow-y: auto; font-size: larger; font-weight: bold;"
                      class="px-5 scrollable-content"
                      @wheel.stop="stopParentScroll"
                      @touchmove.stop
                    >
                      {{ card.step ? card.step.step : '' }}
                    </div>
                  </div>

                  <b-btn
                    class="flipped-btn"
                    style="justify-self:end;text-transform: none; margin-bottom: 41px;"
                    @click.stop="toggleCardFlip(card.id)"
                    ><md-icon class="mr-2">repeat</md-icon>Click to flip</b-btn
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-skeleton-wrapper>

      <pagination
        v-if="totalSkillCards > skillCard.length"
        v-model="currentPage"
        :total-rows="totalSkillCards"
        :per-page="perPage"
        @change="onPageChange"
        class="mb-3"
      />
    </div>
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import UserAvatar from '@/components/User/UserAvatar.vue';
import { get, keyBy, update, debounce } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { formatTime } from '../../../common/utils';
import { USER_ROLES, APPROVAL_STATUS } from '../../../common/constants';
import PageSeparator from '@/components/Ui/PageSeparator.vue';

export default {
  components: {
    Pagination,
    UserAvatar,
    PageSeparator,
  },
  extends: Page,

  data() {
    return {
      title: 'Skill Maps',
      filters: {
        skillmap: '',
        self_skillmap: false,
      },
      USER_ROLES,
      APPROVAL_STATUS,
      totalSkillCards: 0,
      skillCard: [],
      isLoading: false,
      perPage: 16,
      windowWidth: window.innerWidth,
      likeCountLoading: false,
      likesCount: 0,
      isLikingSkillCard: false,
      isUnlikingSkillCard: false,
      flippedCards: {},
      onfirstLoad: true,
      isLoadingSkillmaps: false,
      schoolSkillMaps: [],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    ...mapGetters('aiSkills', ['getSkillCardFilters']),

    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Skills', active: true },
      ];
    },
    isMobSmallScreen() {
      return this.windowWidth <= 420;
    },
    // subLayout() {
    //   return this.windowWidth > 992;
    // },
    // subLayoutDrawer() {
    //   return this.windowWidth > 992 ? FiltersSidebar : Filters;
    // },
    // subLayoutDrawerId() {
    //   return 'filters';
    // },
    // enableSidebarToggle() {
    //   return this.windowWidth <= 992;
    // },
  },
  watch: {
    filters: {
      deep: true,
      handler(filterValues) {
        this.filters.skillmap = filterValues.skillmap;
        this.debouncedSearchSkillCards(this);
        setTimeout(() => {
          this.onfirstLoad = false;
        }, 500);
      },
    },
  },

  methods: {
    ...mapActions('aiSkills', ['getOtherStdSkillCards', 'getAllSkillMaps']),
    ...mapActions('likes', ['getLikesCount', 'createLike', 'unLike']),

    formatTime,
    get,

    debouncedSearchSkillCards: debounce(vm => {
      vm.fetchAiSkillmapCards();
    }, 500),
    async fetchSchoolSkillmaps() {
      this.isLoadingSkillmaps = true;
      const resp = await this.getAllSkillMaps();
      this.schoolSkillMaps = resp.data
        ? resp.data.map(skillmap => {
            return { id: skillmap.id, label: skillmap.title };
          })
        : [];
      this.isLoadingSkillmaps = false;
    },
    async unlikeSkillCard(id) {
      if (this.isLikingSkillCard || this.isUnlikingSkillCard) return;

      this.isUnlikingSkillCard = true;
      try {
        const data = { like_on_id: id, like_on_type: 'student_ai_skill' };

        update(this.likesCount, `${id}.mylike`, () => false);

        await this.unLike(data);
        this.makeToast({ variant: 'success', msg: 'AI Skill Card UnLiked!' });

        this.fetchLikeCount();
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isUnlikingSkillCard = false;
    },

    async likeSkillCard(id) {
      if (this.isLikingSkillCard || this.isUnlikingSkillCard) return;

      this.isLikingSkillCard = true;
      try {
        const data = { like_on_id: id, like_on_type: 'student_ai_skill' };
        update(this.likesCount, `${id}.mylike`, () => true);

        await this.createLike(data);
        this.makeToast({ variant: 'success', msg: 'AI Skill Card Liked!' });
        this.fetchLikeCount();
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLikingSkillCard = false;
    },

    async fetchLikeCount() {
      this.likeCountLoading = true;
      const ids = this.skillCard.map(card => card.id);
      const resp = await this.getLikesCount({
        like_on_type: 'aiskillcard',
        like_on_object_ids: ids,
      });
      this.likesCount = keyBy(resp.data.count, 'like_on_id');
      this.likeCountLoading = false;
    },

    async fetchAiSkillmapCards(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();
      try {
        const response = await this.getOtherStdSkillCards({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...params,
          ...(this.filters.skillmap ? { skill_map__id: this.filters.skillmap } : {}),
          ...(this.filters.self_skillmap ? { student__user__id: this.getLoggedInUser.id } : {}),
        });
        this.skillCard = response.data.results;

        this.currentPage = pageNum;
        this.totalSkillCards = response.data.count;
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + get(response.data, 'results.length', 0) - 1;
        // if (this.$route.params.id) {
        //   this.$nextTick(() => {
        //     const cm = document.getElementById(`skill-${this.$route.params.id}`);
        //     cm.scrollIntoView({ behavior: 'smooth' });
        //   });
        // }
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    // showSkillDetails(id) {
    //   this.$router.push({ name: 'student-skill-details', params: { id } });
    // },

    onPageChange(pageNum) {
      this.fetchAiSkillmapCards(pageNum);
    },

    toggleCardFlip(skillId) {
      this.$set(this.flippedCards, skillId, !this.flippedCards[skillId]);
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    stopParentScroll(event) {
      // Only prevent default if scroll would escape bounds
      const element = event.currentTarget;
      const isAtTop = element.scrollTop === 0;
      const isAtBottom = element.scrollHeight - element.scrollTop === element.clientHeight;

      // If scrolling up at the top or scrolling down at the bottom, prevent bubbling
      if ((event.deltaY < 0 && isAtTop) || (event.deltaY > 0 && isAtBottom)) {
        event.preventDefault();
      }

      // Stop propagation to prevent parent elements from scrolling
      event.stopPropagation();
    },
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    if (this.$route.params.self_skillmap) {
      this.filters.self_skillmap = true;
    }
    await this.fetchAiSkillmapCards();
    this.fetchLikeCount();
    this.fetchSchoolSkillmaps();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
.card-flip {
  perspective: 1000px;
  height: 100%;
}

.card-flip-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.6s;
  transform-style: preserve-3d;
}

.card-flip.flipped .card-flip-inner {
  transform: rotateY(180deg);
}

.card-flip-front,
.card-flip-back {
  position: absolute;
  width: 100%;
  height: 100%;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

.card-flip-back {
  transform: rotateY(180deg);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
}

.card-flip-back h4 {
  margin-bottom: 1rem;
}

.card-flip-back p {
  margin-bottom: 0.75rem;
}

.flip-icon {
  position: absolute;
  bottom: 65px;
  align-self: flex-end;
  justify-self: anchor-center;
  padding: 6px;
  cursor: pointer;
  text-transform: none;
  border: none;
  color: white;
  background-color: rgb(0, 0, 0, 0.5);
  transition: background-color 0.3s;
}
.flip-icon:hover {
  opacity: 1;
  background-color: black;
}
.flipped-btn {
  color: white;
  border: none;
  background-color: rgb(0, 0, 0, 0.7);
  transition: background-color 0.3s;
}
.flipped-btn:hover {
  opacity: 1;
  background-color: black;
}

.scrollable-content {
  background-color: #f1f1f1;
  border-radius: 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  padding: 15px;
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 15px;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  /* This is key to prevent scroll bubbling */
  overscroll-behavior: contain;
  scroll-behavior: smooth;
}

@media (max-width: 767.98px) {
  .card-flip-back {
    padding: 1rem;
  }

  .card-body {
    padding: 0.75rem;
  }

  .card-title {
    font-size: 0.9rem;
  }

  .text-muted {
    font-size: 0.8rem;
  }
}
</style>
